import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiVideo, FiMapPin, FiDollarSign, FiSearch, FiShield, FiCreditCard, FiAward, FiPercent, FiArrowRight, FiTrendingUp, FiCalendar } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import styles from './UserDashboard.module.css';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useCurrency } from '../../app/CurrencyContext';
import Pagination from '../../components/Pagination/Pagination';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import RequestMeetingModal from '../../components/RequestMeetingModal/RequestMeetingModal';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTheme } from '../../app/ThemeContext';
import { fetchMyLeads } from '../../features/leads/leadsSlice';
import axiosClient from '../../api/axiosClient';

const iconFor = (leadType: string, meetingType: string | null) => {
  if (leadType === 'invest') return <FiDollarSign />;
  return meetingType === 'vc' ? <FiVideo /> : <FiMapPin />;
};

const labelFor = (leadType: string, meetingType: string | null) => {
  if (leadType === 'invest') return 'Investment request';
  return meetingType === 'vc' ? 'VC / Venture meeting' : 'Physical meeting';
};

const UserDashboard: React.FC = () => {
  const { formatFromInr, convertFromInr, formatTransaction, format } = useCurrency();
  const dispatch = useAppDispatch();
  const { myLeads, status } = useAppSelector((state) => state.leads);
  const { theme } = useTheme();

  const pieData = [
    { name: 'Pending', value: myLeads.filter((l) => l.status === 'pending').length, color: '#e8a86a' },
    { name: 'Confirmed', value: myLeads.filter((l) => l.status === 'confirmed').length, color: '#1d60f6' },
    { name: 'Rejected', value: myLeads.filter((l) => l.status === 'rejected').length, color: '#e2534a' },
  ].filter((d) => d.value > 0);
  const { user } = useAppSelector((state) => state.auth);
  const [range, setRange] = useState<'day' | 'week' | 'month'>('month');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const PAGE_SIZE = 6;

  const [summary, setSummary] = useState({
    kycStatus: null as string | null,
    kybStatus: null as string | null,
    balance: 0,
    partnerCount: 0,
    partnerApproved: 0,
    rebateTotal: 0,
    rebateClaimable: 0,
    portalActiveCount: 0,
    portalNextDueAmount: null as number | null,
    portalNextDueDate: null as string | null,
    portalInvestedTotal: 0,
    locked: 0,
    withdrawable: 0,
  });

  useEffect(() => {
    Promise.all([
      axiosClient.get('/kyc/documents').catch(() => ({ data: { documents: [] } })),
      axiosClient.get('/kyb/profile').catch(() => ({ data: { profile: null } })),
      axiosClient.get('/funds/balance').catch(() => ({ data: { availableInr: 0 } })),
      axiosClient.get('/partners/mine').catch(() => ({ data: { applications: [] } })),
      axiosClient.get('/rebate/mine').catch(() => ({ data: { totalBonus: 0, claimable: 0 } })),
      axiosClient.get('/portal/mine').catch(() => ({ data: { investments: [] } })),
    ]).then(([kyc, kyb, balance, partners, rebate, portal]) => {
      const docs = kyc.data.documents || [];
      const investments = portal.data.investments || [];
      const active = investments.filter((i: any) => i.status === 'active');
      const withNextDue = active.filter((i: any) => i.next_due_date).sort(
        (a: any, b: any) => new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime()
      );
      setSummary({
        kycStatus: docs.length > 0 ? docs[0].status : null,
        kybStatus: kyb.data.profile?.status || null,
        balance: balance.data.availableInr || 0,
        partnerCount: (partners.data.applications || []).length,
        partnerApproved: (partners.data.applications || []).filter((a: any) => a.status === 'approved').length,
        rebateTotal: rebate.data.totalBonus || 0,
        rebateClaimable: rebate.data.claimable || 0,
        portalActiveCount: active.length,
        portalNextDueAmount: withNextDue[0]?.next_due_amount ? Number(withNextDue[0].next_due_amount) : null,
        portalNextDueDate: withNextDue[0]?.next_due_date || null,
        portalInvestedTotal: active.reduce((sum: number, i: any) => sum + Number(i.invest_amount || 0), 0),
        locked: balance.data.lockedInr || 0,
        withdrawable: balance.data.withdrawableInr ?? balance.data.availableInr ?? 0,
      });
    });
  }, []);

  const rangeDays = range === 'day' ? 1 : range === 'week' ? 7 : 30;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - rangeDays);
  const rangedLeads = myLeads.filter((l) => new Date(l.created_at) >= cutoff);
  const sortedInvest = [...myLeads]
    .filter((l) => l.lead_type === 'invest')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  let running = 0;
  const areaData = sortedInvest.map((l) => {
    running += convertFromInr(Number(l.amount_inr_equivalent ?? l.amount) || 0);
    return { date: new Date(l.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), total: running };
  });
  const totalInvested = rangedLeads.reduce((sum, l) => sum + convertFromInr(Number(l.amount_inr_equivalent ?? l.amount) || 0), 0);

  const filteredLeads = myLeads.filter((l) =>
    !search || labelFor(l.lead_type, l.meeting_type).toLowerCase().includes(search.toLowerCase()) || l.status.includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const pagedLeads = filteredLeads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    dispatch(fetchMyLeads());
  }, [dispatch]);

  return (
    <DashboardShell>
      <PageHeader
        title={`Hi ${user?.full_name?.split(' ')[0] || 'there'}`}
        subtitle="Here's the status of everything you've submitted."
        action={<WalletBadge />}
      />
      <div className={styles.page}>
        <div className={styles.portfolioCard}>
          <div>
            <span className={styles.portfolioLabel}>Total Portfolio Value</span>
            <b className={styles.portfolioValue}>
              {/* Portal money already sits inside the wallet, so it is never added a second time. */}
              {formatFromInr(Math.round(summary.balance + summary.locked + summary.rebateClaimable))}
            </b>
          </div>
          <div className={styles.portfolioBreakdown}>
            <div>
              <span>Wallet</span>
              <b>{formatFromInr(Math.round(summary.balance))}</b>
            </div>
            <div>
              <span>Portal A/B/C (inside wallet)</span>
              <b>{formatFromInr(Math.round(summary.portalInvestedTotal))}</b>
            </div>
            <div>
              <span>Rebate (unclaimed)</span>
              <b>{formatFromInr(Math.round(summary.rebateClaimable))}</b>
            </div>
          </div>
        </div>

        <div className={styles.summaryGrid}>
          <Link to="/dashboard/kyc" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconBlue}`}><FiShield /></div>
            <div>
              <span>KYC Status</span>
              <b>{summary.kycStatus ? summary.kycStatus[0].toUpperCase() + summary.kycStatus.slice(1) : 'Not started'}</b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
          <Link to="/dashboard/kyc" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconPurple}`}><FiShield /></div>
            <div>
              <span>KYB Status</span>
              <b>{summary.kybStatus ? summary.kybStatus[0].toUpperCase() + summary.kybStatus.slice(1) : 'Not started'}</b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
          <Link to="/dashboard/funds" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconGreen}`}><FiCreditCard /></div>
            <div>
              <span>Withdrawable Balance</span>
              <b>{formatFromInr(Math.round(summary.withdrawable))}</b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
          <Link to="/dashboard/partners" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconPurple}`}><FiAward /></div>
            <div>
              <span>Partner Applications</span>
              <b>{summary.partnerCount === 0 ? 'None yet' : `${summary.partnerApproved} approved of ${summary.partnerCount}`}</b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
          <Link to="/dashboard/rebate" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconOrange}`}><FiPercent /></div>
            <div>
              <span>Rebate Earnings</span>
              <b>{formatFromInr(summary.rebateTotal)} <small>({formatFromInr(summary.rebateClaimable)} claimable)</small></b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
          <Link to="/dashboard/portal-investments" className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${styles.summaryIconBlue}`}><FiTrendingUp /></div>
            <div>
              <span>{summary.portalNextDueAmount ? 'Next ROI due' : 'Portal Investments'}</span>
              <b>
                {summary.portalActiveCount === 0
                  ? 'None yet'
                  : summary.portalNextDueAmount
                    ? `${formatFromInr(summary.portalNextDueAmount)} due ${summary.portalNextDueDate ? new Date(summary.portalNextDueDate).toLocaleDateString() : ''}`
                    : `${summary.portalActiveCount} active`}
              </b>
            </div>
            <FiArrowRight className={styles.summaryArrow} />
          </Link>
        </div>

        <div className={styles.sectionHeadRow}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Meeting &amp; Investment Requests</h2>
          <button className={styles.meetingBtn} onClick={() => setShowMeetingModal(true)}>
            <FiCalendar /> Request a Meeting
          </button>
        </div>
        {status !== 'loading' && myLeads.length > 0 && (
          <div className={styles.statsRow}>
            <div className={styles.statPill}>
              <b>{myLeads.length}</b>
              <span>Total requests</span>
            </div>
            <div className={styles.statPill}>
              <b className={styles.pendingColor}>{myLeads.filter((l) => l.status === 'pending').length}</b>
              <span>Pending</span>
            </div>
            <div className={styles.statPill}>
              <b className={styles.confirmedColor}>{myLeads.filter((l) => l.status === 'confirmed').length}</b>
              <span>Confirmed</span>
            </div>
            <div className={styles.statPill}>
              <b>{format(myLeads.reduce((sum, l) => sum + convertFromInr(Number(l.amount_inr_equivalent ?? l.amount) || 0), 0))}</b>
              <span>Total requested</span>
            </div>
          </div>
        )}

        {status !== 'loading' && myLeads.length > 0 && (
          <div className={styles.heroCard}>
            <div className={styles.heroTop}>
              <div>
                <div className={styles.heroLabel}>Total requested</div>
                <div className={styles.heroValue}>{format(totalInvested)}</div>
                <div className={styles.heroSub}>{rangedLeads.length} request{rangedLeads.length !== 1 ? 's' : ''} in the last {range}</div>
              </div>
              <div className={styles.rangeToggle}>
                {(['day', 'week', 'month'] as const).map((r) => (
                  <button key={r} className={range === r ? styles.rangeActive : ''} onClick={() => setRange(r)}>
                    {r[0].toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={areaData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1d60f6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1d60f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? 'rgba(15,23,42,.08)' : 'rgba(255,255,255,.06)'} vertical={false} />
                <XAxis dataKey="date" stroke={theme === 'light' ? '#4b5768' : '#8391a3'} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis hide />
                <Tooltip contentStyle={{ background: theme === 'light' ? '#ffffff' : '#0d1420', border: theme === 'light' ? '1px solid rgba(15,23,42,.12)' : '1px solid rgba(255,255,255,.12)', borderRadius: 10, color: theme === 'light' ? '#0f1420' : '#eaf2f8' }} formatter={(v: number) => [format(v), 'Total']} />
                <Area type="monotone" dataKey="total" stroke="#1d60f6" strokeWidth={2} fill="url(#heroFill)" />
              </AreaChart>
            </ResponsiveContainer>
            {areaData.length === 0 && <div className={styles.heroEmpty}>No investment history yet — this fills in once a request is confirmed.</div>}
          </div>
        )}

        {status !== 'loading' && myLeads.length > 0 && (
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Your requests by status</div>
            <div style={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={62}
                    outerRadius={90}
                    paddingAngle={pieData.length > 1 ? 4 : 0}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: theme === 'light' ? '#ffffff' : '#0d1420', border: theme === 'light' ? '1px solid rgba(15,23,42,.12)' : '1px solid rgba(255,255,255,.12)', borderRadius: 10, color: theme === 'light' ? '#0f1420' : '#eaf2f8' }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: theme === 'light' ? '#4b5768' : '#8391a3' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.chartCenter}>
                <div className={styles.chartCenterNum}>{myLeads.length}</div>
                <div className={styles.chartCenterLbl}>Total</div>
              </div>
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className={styles.loading}><span className={styles.spinnerLg} /></div>
        )}

        {status !== 'loading' && myLeads.length === 0 && (
          <div className={styles.empty}>
            No requests yet. Use "Request a Meeting" above, or visit the Investment Dashboard to submit an investment request.
          </div>
        )}

        {status !== 'loading' && myLeads.length > 0 && (
          <div className={styles.listHead}>
            <span>Your requests</span>
            <div className={styles.tableSearchWrap}>
              <FiSearch />
              <input placeholder="Search by type or status..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
          </div>
        )}

        {status !== 'loading' && myLeads.length > 0 && (
          <div className={styles.list}>
            {pagedLeads.map((lead, i) => (
              <div className={styles.card} key={lead.id} style={{ animationDelay: `${i * 0.06}s` }}>
                <div className={styles.cardLeft}>
                  <div className={styles.iconBox}>{iconFor(lead.lead_type, lead.meeting_type)}</div>
                  <div>
                    <div className={styles.type}>{labelFor(lead.lead_type, lead.meeting_type)}</div>
                    <div className={styles.meta}>
                      Submitted {new Date(lead.created_at).toLocaleDateString()}
                      {lead.amount ? ` · ${formatTransaction(Number(lead.amount), lead.currency || 'INR', Number(lead.amount_inr_equivalent ?? lead.amount))}` : ''}
                    </div>
                    {lead.message && (
                      <ul className={styles.detailList}>
                        {lead.message.split(' | ').filter(Boolean).map((part, idx) => (
                          <li key={idx}>{part}</li>
                        ))}
                      </ul>
                    )}
                    {lead.status === 'confirmed' && lead.contact_by && (
                      <div className={styles.contactBy}>
                        We'll contact you by {new Date(lead.contact_by).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <StatusBadge status={lead.status} />
              </div>
            ))}
          </div>
        )}
        {status !== 'loading' && filteredLeads.length > 0 && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {showMeetingModal && (
        <RequestMeetingModal
          onClose={() => setShowMeetingModal(false)}
          onSuccess={() => dispatch(fetchMyLeads())}
        />
      )}
    </DashboardShell>
  );
};

export default UserDashboard;